/**
 * P&L Review → multi-sheet Excel export ("download the entire P&L").
 *
 * Fetches all five P&L tabs fresh for the chosen facility/month (so the export
 * is the WHOLE review, not just the active tab, and not limited to page 1), then
 * builds a workbook: a Summary cover sheet + one sheet per tab. Reuses the
 * already-installed `xlsx`. Totals shown on screen are server-computed (EBITDA
 * report, clientWiseData) — we surface those directly so the file matches the UI.
 *
 * NOTE (contract): the three paginated endpoints are fetched with allResults/
 * high limits and a page-loop fallback so nothing is silently truncated. When
 * the backend swagger contract lands, swap PAndLApiService for the generated
 * client — the sheet builders below stay the same.
 */
import * as XLSX from 'xlsx';
import {
	ClientWisePLResponse,
	EBITDAResponse,
	EscalationResponse,
	PAndLApiService,
	RevenueInUnitsResponse,
	RevenueRecord,
	RevenueResponse,
} from '../../../services/pAndLApi';

export interface PLExportParams {
	cityId: number;
	facilityId: number;
	facilityName: string;
	month: string; // e.g. "April"
	year: string; // e.g. "2026"
	startDate: string; // YYYY-MM-DD
	endDate: string; // YYYY-MM-DD
}

type AOA = (string | number)[][];

const num = (v: unknown): number => {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
};

/** Pull the Expenditure records out of the deeply-nested getRevenue response. */
const expenditureRecords = (r?: RevenueResponse): RevenueRecord[] =>
	r?.data?.[0]?.facilities?.[0]?.monthYearData?.[0]?.records ?? [];

/** getRevenue: fetch every page (Expenditure can paginate). */
async function fetchAllRevenue(p: PLExportParams): Promise<RevenueResponse | undefined> {
	try {
		const first = await PAndLApiService.getRevenue({
			facility_id: p.facilityId,
			start_date: p.startDate,
			end_date: p.endDate,
			page: 1,
			limit: 1000,
			allResults: true,
		});
		const total = first?.pagination?.totalPages ?? 1;
		if (total <= 1) return first;
		// page-loop fallback if allResults was ignored
		for (let pg = 2; pg <= total; pg++) {
			const next = await PAndLApiService.getRevenue({
				facility_id: p.facilityId,
				start_date: p.startDate,
				end_date: p.endDate,
				page: pg,
				limit: 1000,
			});
			const recs = expenditureRecords(next);
			const into = expenditureRecords(first);
			into.push(...recs);
		}
		return first;
	} catch {
		return undefined;
	}
}

const safe = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
	try {
		return await fn();
	} catch {
		return undefined;
	}
};

/** Build the Expenditure sheet rows. */
function expenditureSheet(r?: RevenueResponse): AOA {
	const recs = expenditureRecords(r);
	const head = ['Sl', 'Costing Type', 'Projection', 'W1', 'Δ W1', 'W2', 'Δ W2', 'W3', 'Δ W3', 'W4', 'Δ W4', 'Total Actual', 'Total Δ'];
	const rows: AOA = [head];
	recs.forEach((x, i) =>
		rows.push([
			i + 1,
			x.costingTypeName,
			num(x.projected_value),
			num(x.week1_actual_value),
			x.week1_delta_with_percentage ?? '',
			num(x.week2_actual_value),
			x.week2_delta_with_percentage ?? '',
			num(x.week3_actual_value),
			x.week3_delta_with_percentage ?? '',
			num(x.week4_actual_value),
			x.week4_delta_with_percentage ?? '',
			num(x.total_actual_value),
			x.total_delta_with_percentage ?? '',
		])
	);
	if (recs.length) {
		rows.push([
			'',
			'TOTAL',
			recs.reduce((s, x) => s + num(x.projected_value), 0),
			recs.reduce((s, x) => s + num(x.week1_actual_value), 0),
			'',
			recs.reduce((s, x) => s + num(x.week2_actual_value), 0),
			'',
			recs.reduce((s, x) => s + num(x.week3_actual_value), 0),
			'',
			recs.reduce((s, x) => s + num(x.week4_actual_value), 0),
			'',
			recs.reduce((s, x) => s + num(x.total_actual_value), 0),
			'',
		]);
	}
	return rows.length > 1 ? rows : [['No Expenditure data for this period']];
}

/** Unit Economics sheet. */
function unitEconomicsSheet(r?: RevenueInUnitsResponse): AOA {
	const items = r?.updateUnitEconomics ?? [];
	const head = ['Sl', 'Costing Type', 'Projection', 'W1', 'Δ W1', 'W2', 'Δ W2', 'W3', 'Δ W3', 'W4', 'Δ W4', 'Aggr Unit'];
	const rows: AOA = [head];
	items.forEach((x, i) =>
		rows.push([
			i + 1,
			x.costingTypeName,
			num(x.projectValue),
			num(x.week1_actual_value),
			x.week1Delta ?? '',
			num(x.week2_actual_value),
			x.week2Delta ?? '',
			num(x.week3_actual_value),
			x.week3Delta ?? '',
			num(x.week4_actual_value),
			x.week4Delta ?? '',
			num(x.aggrUnit),
		])
	);
	const t = r?.total;
	if (t) {
		rows.push([
			'',
			'TOTAL',
			num(t.projectedUnitValueTotal),
			num(t.week1UnitTotal),
			'',
			num(t.week2UnitTotal),
			'',
			num(t.week3UnitTotal),
			'',
			num(t.week4UnitTotal),
			'',
			num(t.aggrUnitTotal),
		]);
	}
	return rows.length > 1 ? rows : [['No Unit Economics data for this period']];
}

/** EBITDA sheet — the variable + indirect tables and the summary lines, per month. */
function ebitdaSheet(r?: EBITDAResponse): AOA {
	const report = r?.report ?? {};
	const months = Object.keys(report);
	if (!months.length) return [['No EBITDA data for this period']];
	const rows: AOA = [];
	const head = ['Costing Type', 'W1', 'W2', 'W3', 'W4', 'Cumulative', 'Estimated'];
	for (const m of months) {
		const rep = report[m];
		rows.push([`${m} — Variable Cost Details`]);
		rows.push(head);
		rep.variableCostDetails?.forEach(x =>
			rows.push([x.costingTypeName, num(x.week1_actual_value), num(x.week2_actual_value), num(x.week3_actual_value), num(x.week4_actual_value), num(x.total_actual_value), num(x.projectedValue)])
		);
		const wk = (w: { week1: number; week2: number; week3: number; week4: number; total: number }) => [num(w.week1), num(w.week2), num(w.week3), num(w.week4), num(w.total)];
		rows.push(['Total Revenue', ...wk(rep.totalRevenue), '']);
		rows.push(['Total Variable Cost', ...wk(rep.totalVariableCost), '']);
		rows.push(['Contribution', ...wk(rep.totalContribution), '']);
		rows.push([`${m} — Indirect Expense Details`]);
		rows.push(head);
		rep.indirectExpenseDetails?.forEach(x =>
			rows.push([x.costingTypeName, num(x.week1_actual_value), num(x.week2_actual_value), num(x.week3_actual_value), num(x.week4_actual_value), num(x.total_actual_value), num(x.projectedValue)])
		);
		rows.push(['Total Indirect Expense', ...wk(rep.totalIndirectExpenseCost), '']);
		rows.push(['EBITDA', ...wk(rep.EBITDA), '']);
		rows.push([]);
	}
	return rows;
}

const COST_KEYS: Array<keyof ClientWisePLResponse['clientWiseData']['data'][number]> = [
	'Manpower', 'Electricity', 'Water', 'Consumables', 'Chemicals', 'Logistics', 'On Site Manpower',
];

/** Client-Wise P&L sheet. */
function clientWiseSheet(r?: ClientWisePLResponse): AOA {
	const clients = r?.clientWiseData?.data ?? [];
	const head = ['Sl', 'Client', 'SKU', 'OPD', 'Price', 'Rev/Plate', ...COST_KEYS.map(String), 'Contribution', 'Margin %'];
	const rows: AOA = [head];
	clients.forEach((c, i) =>
		rows.push([
			i + 1,
			c.clientName,
			num(c.skuCount),
			num(c.opdCount),
			num(c.price),
			num(c.revPerPlate),
			...COST_KEYS.map(k => num(c[k])),
			num(c.contribution),
			c.marginPercentage == null ? '' : num(c.marginPercentage),
		])
	);
	return rows.length > 1 ? rows : [['No Client-Wise P&L data for this period']];
}

/** Escalations sheet — the week-wise totals (whole-dataset summaries). */
function escalationsSheet(r?: EscalationResponse): AOA {
	if (!r) return [['No Escalation data for this period']];
	const t = r.totalEscalationWeekWise;
	const rows: AOA = [
		['Total escalations', num(r.totalEscalations)],
		[],
		['', 'W1', 'W2', 'W3', 'W4'],
		['Δ by week', num(t?.week1?.delta), num(t?.week2?.delta), num(t?.week3?.delta), num(t?.week4?.delta)],
	];
	return rows;
}

/** Summary cover sheet — facility/period + the EBITDA bottom line. */
function summarySheet(p: PLExportParams, ebitda?: EBITDAResponse): AOA {
	const rows: AOA = [
		['P&L Review — Export'],
		[],
		['Facility', p.facilityName],
		['Period', `${p.month} ${p.year}`],
		['Date range', `${p.startDate} to ${p.endDate}`],
		['Generated', new Date().toLocaleString('en-GB')],
		[],
		['Headline (from EBITDA report)'],
		['', 'W1', 'W2', 'W3', 'W4', 'Total'],
	];
	const months = Object.keys(ebitda?.report ?? {});
	if (months.length) {
		const rep = ebitda!.report[months[0]];
		const line = (label: string, w: { week1: number; week2: number; week3: number; week4: number; total: number }) =>
			rows.push([label, num(w.week1), num(w.week2), num(w.week3), num(w.week4), num(w.total)]);
		line('Total Revenue', rep.totalRevenue);
		line('Total Variable Cost', rep.totalVariableCost);
		line('Contribution', rep.totalContribution);
		line('Total Indirect Expense', rep.totalIndirectExpenseCost);
		line('EBITDA', rep.EBITDA);
	} else {
		rows.push(['No EBITDA data for this period']);
	}
	return rows;
}

function addSheet(wb: XLSX.WorkBook, name: string, aoa: AOA) {
	const ws = XLSX.utils.aoa_to_sheet(aoa);
	// rough auto-width
	const widths: number[] = [];
	aoa.forEach(row => row.forEach((cell, i) => {
		widths[i] = Math.min(Math.max(widths[i] ?? 10, String(cell ?? '').length + 2), 40);
	}));
	ws['!cols'] = widths.map(wch => ({ wch }));
	XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31)); // Excel 31-char sheet-name limit
}

/**
 * Fetch all five tabs and download a multi-sheet P&L workbook.
 * Returns the filename written (for toasts/logging).
 */
export async function exportPLToExcel(p: PLExportParams): Promise<string> {
	const [revenue, units, ebitda, clientWise, escalations] = await Promise.all([
		fetchAllRevenue(p),
		safe<RevenueInUnitsResponse>(() => PAndLApiService.getRevenueInUnits({ city_id: p.cityId, facility_id: p.facilityId, start_date: p.startDate, end_date: p.endDate, page: 1, limit: 1000 })),
		safe<EBITDAResponse>(() => PAndLApiService.getEBITDA({ city_id: p.cityId, facility_id: p.facilityId, start_date: p.startDate, end_date: p.endDate, groupByClient: true })),
		safe<ClientWisePLResponse>(() => PAndLApiService.getClientWisePL({ city_id: p.cityId, facility_id: p.facilityId, start_date: p.startDate, end_date: p.endDate, groupByClient: true })),
		safe<EscalationResponse>(() => PAndLApiService.getEscalation({ cityId: p.cityId, facilityId: p.facilityId, startDate: p.startDate, endDate: p.endDate })),
	]);

	const wb = XLSX.utils.book_new();
	addSheet(wb, 'Summary', summarySheet(p, ebitda));
	addSheet(wb, 'EBITDA', ebitdaSheet(ebitda));
	addSheet(wb, 'Expenditure', expenditureSheet(revenue));
	addSheet(wb, 'Unit Economics', unitEconomicsSheet(units));
	addSheet(wb, 'Client-Wise P&L', clientWiseSheet(clientWise));
	addSheet(wb, 'Escalations', escalationsSheet(escalations));

	const fac = p.facilityName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
	const filename = `PL_${fac}_${p.month}_${p.year}.xlsx`;
	XLSX.writeFile(wb, filename);
	return filename;
}
